<template>
  <div class="roster-import-wrapper">
    <CCard color="success" class="roster-guide-card roster-import-card text-white">
      <CCardHeader class="roster-guide-card__header roster-import-card__header">
        <strong>勤務表ファイル取込</strong>
        <span class="roster-guide-card__badge">CSV / TSV</span>
      </CCardHeader>
      <CCardBody class="roster-guide-card__body roster-import-card__body">
        <p class="roster-import-card__lead">
          列名や並び順が異なるファイルも、取込前に項目を割り当てて内容を確認できます。
        </p>
        <div class="roster-import-card__actions">
          <label
            class="btn btn-light btn-sm roster-import-file-button"
            :class="{ disabled: disabled }"
          >
            ファイルを選択
            <input
              type="file"
              accept=".tsv,.csv,.txt,text/tab-separated-values,text/csv,text/plain"
              :disabled="disabled"
              @change="onFileSelected"
            />
          </label>
          <button
            type="button"
            class="btn btn-outline-light btn-sm"
            :disabled="disabled"
            @click="downloadTemplate"
          >
            標準TSVをダウンロード
          </button>
        </div>
        <!--
          ［標準TSVをダウンロード］で落ちるファイルの説明（文は rosterImportTexts.js。空なら出さない）。
          落ちるのは日付だけの記入用の表で、見本だと思ってそのまま取り込むと「反映できるデータが
          ありません。」になる（2026-10-11）。
        -->
        <p v-if="templateNote" class="roster-import-card__support roster-import-card__template-note">
          {{ templateNote }}
        </p>
        <p class="roster-import-card__support">
          UTF-8・Shift_JIS・UTF-16、タブ・カンマ・セミコロン区切りに対応しています。
          ExcelブックはCSVまたはTSVで保存してから選択してください。
        </p>
        <p v-if="lastImportMessage" class="roster-import-card__result" role="status">
          {{ lastImportMessage }}
        </p>
      </CCardBody>
    </CCard>

    <CModal
      :show.sync="modalOpen"
      centered
      :close-on-backdrop="false"
      class="roster-import-modal preika-modal-xl"
    >
      <!--
        CoreUI 3.0.0 に CModalHeader / CModalBody は無い（未登録の要素として本文へ落ち、
        題名が見出しの帯に入らなかった）。ファイル名も出すので title ではなく #header を使う。
        #header を差し替えると既定の × が消えるので、既定と同じ CButtonClose を自分で置く。
      -->
      <template #header>
        <h5 class="modal-title">
          <strong>勤務表ファイル取込</strong>
          <small v-if="sourceName" class="roster-import-modal__filename">{{ sourceName }}</small>
        </h5>
        <CButtonClose @click="modalOpen = false"/>
      </template>

      <div>
        <div v-if="fileError" class="alert alert-danger" role="alert">
          {{ fileError }}
        </div>

        <template v-else-if="records.length">
          <section class="roster-import-section">
            <div class="roster-import-section__heading">
              <div>
                <span class="roster-import-step">1</span>
                <h4>ファイル形式を確認</h4>
              </div>
              <span class="roster-import-summary">
                {{ dataRowCount }}行 / {{ sourceColumns.length }}列
              </span>
            </div>

            <div class="roster-import-format-grid">
              <label>
                <span>文字コード</span>
                <select v-model="encodingMode" class="custom-select" @change="reparseSource">
                  <option value="auto">自動判定</option>
                  <option value="utf-8">UTF-8</option>
                  <option value="shift_jis">Shift_JIS</option>
                  <option value="utf-16le">UTF-16 LE</option>
                  <option value="utf-16be">UTF-16 BE</option>
                </select>
                <small>判定結果: {{ encodingLabel }}</small>
              </label>

              <label>
                <span>区切り文字</span>
                <select v-model="delimiterMode" class="custom-select" @change="reparseSource">
                  <option value="auto">自動判定</option>
                  <option :value="'\t'">タブ</option>
                  <option value=",">カンマ</option>
                  <option value=";">セミコロン</option>
                  <option value="|">縦線</option>
                </select>
                <small>判定結果: {{ detectedDelimiterLabel }}</small>
              </label>

              <label class="roster-import-check">
                <input v-model="hasHeader" type="checkbox" @change="onHeaderModeChanged" />
                <span>見出し行を使用</span>
              </label>

              <label v-if="hasHeader">
                <span>見出し行</span>
                <select v-model.number="headerRecordIndex" class="custom-select" @change="resetMappings">
                  <option
                    v-for="option in headerRowOptions"
                    :key="option.index"
                    :value="option.index"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <small v-if="headerRecordIndex > 0">見出しより前の行は読み飛ばします。</small>
              </label>

              <label>
                <span>日付の決め方</span>
                <select v-model="rowMode" class="custom-select">
                  <option value="date">日付列を使用</option>
                  <option value="sequence">1行目を1日、2行目を2日として使用</option>
                </select>
              </label>
            </div>

            <ul v-if="decodeWarnings.length" class="roster-import-notes">
              <li v-for="warning in decodeWarnings" :key="warning">{{ warning }}</li>
            </ul>
          </section>

          <section class="roster-import-section">
            <div class="roster-import-section__heading">
              <div>
                <span class="roster-import-step">2</span>
                <h4>列を勤務表の項目へ割り当て</h4>
              </div>
              <span class="roster-import-summary">使わない列は「未使用」のままで構いません</span>
            </div>

            <div class="roster-import-mapping-grid">
              <label
                v-for="field in importFields"
                :key="field.key"
                :class="{ 'roster-import-mapping--disabled': rowMode === 'sequence' && field.key === 'date' }"
              >
                <span>
                  {{ field.label }}
                  <em v-if="field.key === 'date' && rowMode === 'date'">必須</em>
                </span>
                <select
                  v-model="mappings[field.key]"
                  class="custom-select"
                  :disabled="rowMode === 'sequence' && field.key === 'date'"
                  @change="onMappingChanged"
                >
                  <option :value="null">未使用</option>
                  <option
                    v-for="column in sourceColumns"
                    :key="column.index"
                    :value="column.index"
                  >
                    {{ column.label }}
                  </option>
                </select>
              </label>
            </div>

            <div v-if="mappingErrors.length" class="alert alert-danger roster-import-alert">
              <ul>
                <li v-for="error in mappingErrors" :key="error">{{ error }}</li>
              </ul>
            </div>
          </section>

          <!--
            名称の変換の段は、自動で読めない名称があるときだけ出る。
            **後ろの段の番号は、出ている段の通し番号にする**（nameConversionShown）。
            固定の 4・5 だと、この段が出ないとき 1 → 2 → 4 → 5 と 3 が抜けて見えた（2026-10-11）。
          -->
          <section
            v-if="nameConversionShown"
            class="roster-import-section"
          >
            <div class="roster-import-section__heading">
              <div>
                <span class="roster-import-step">3</span>
                <h4>ファイル内の名称を変換</h4>
              </div>
              <span class="roster-import-summary">自動判定できない名称だけ表示しています</span>
            </div>

            <div class="roster-import-value-groups">
              <div v-if="unresolvedAttendance1.length" class="roster-import-value-group">
                <h5>勤怠区分</h5>
                <label v-for="item in unresolvedAttendance1" :key="item.key">
                  <span>{{ item.raw }}</span>
                  <select
                    :value="attendanceOverrides[item.key] || ''"
                    class="custom-select"
                    @change="setOverride(attendanceOverrides, item.key, $event.target.value)"
                  >
                    <option value="">変換先を選択</option>
                    <option v-for="choice in attendanceChoices" :key="choice.key" :value="choice.key">
                      {{ choice.label }}
                    </option>
                  </select>
                </label>
              </div>

              <div v-if="unresolvedAttendance2.length" class="roster-import-value-group">
                <h5>勤怠2</h5>
                <label v-for="item in unresolvedAttendance2" :key="item.key">
                  <span>{{ item.raw }}</span>
                  <select
                    :value="attendance2Overrides[item.key] || ''"
                    class="custom-select"
                    @change="setOverride(attendance2Overrides, item.key, $event.target.value)"
                  >
                    <option value="">変換先を選択</option>
                    <option v-for="choice in attendance2Choices" :key="choice.key" :value="choice.key">
                      {{ choice.label }}
                    </option>
                  </select>
                </label>
              </div>

              <div v-if="unresolvedProjects.length" class="roster-import-value-group">
                <h5>プロジェクト</h5>
                <label v-for="item in unresolvedProjects" :key="item.key">
                  <span>{{ item.raw }}</span>
                  <select
                    :value="projectOverrides[item.key] || '__keep__'"
                    class="custom-select"
                    @change="setOverride(projectOverrides, item.key, $event.target.value)"
                  >
                    <option value="__keep__">現在の選択を維持</option>
                    <option v-for="project in projectOptions" :key="project.id" :value="project.id">
                      {{ project.name }}<template v-if="project.external">（現在所属外）</template>
                    </option>
                  </select>
                </label>
              </div>
            </div>
          </section>

          <section class="roster-import-section">
            <div class="roster-import-section__heading">
              <div>
                <span class="roster-import-step">{{ applyMethodStepNo }}</span>
                <h4>反映方法を選択</h4>
              </div>
            </div>

            <div class="roster-import-options">
              <label>
                <input v-model="overwriteMode" type="radio" value="empty" />
                <span>
                  <strong>空欄だけに反映</strong>
                  <small>勤務表に入力済みの値は変更しません。初回はこちらを推奨します。</small>
                </span>
              </label>
              <label>
                <input v-model="overwriteMode" type="radio" value="overwrite" />
                <span>
                  <strong>ファイルの値で上書き</strong>
                  <small>ファイルに値がある項目だけを上書きします。</small>
                </span>
              </label>
              <label class="roster-import-check roster-import-check--option">
                <input v-model="fillRestFromSchedule" type="checkbox" />
                <span>
                  <strong>休憩をプロジェクト設定で補完</strong>
                  <small>ファイルで指定のない日や時刻が変わる日は、通常の勤務表入力と同じ休憩を計算します。</small>
                </span>
              </label>
            </div>
          </section>

          <section class="roster-import-section roster-import-section--preview">
            <div class="roster-import-section__heading">
              <div>
                <span class="roster-import-step">{{ applyMethodStepNo + 1 }}</span>
                <h4>取込結果を確認</h4>
              </div>
              <div class="roster-import-counts">
                <span class="roster-import-count roster-import-count--success">反映 {{ importResult.validRows.length }}日</span>
                <span class="roster-import-count roster-import-count--warning">注意 {{ importResult.warningCount }}件</span>
                <span class="roster-import-count roster-import-count--error">エラー {{ importResult.errorCount }}件</span>
              </div>
            </div>

            <div v-if="importResult.errors.length" class="alert alert-danger roster-import-alert">
              <ul>
                <li v-for="error in importResult.errors" :key="error">{{ error }}</li>
              </ul>
            </div>

            <div class="table-responsive roster-import-preview">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>元の行</th>
                    <th>勤務日</th>
                    <th>開始</th>
                    <th>終了</th>
                    <th>休憩</th>
                    <th>勤怠</th>
                    <th>プロジェクト</th>
                    <th>判定</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in previewRows"
                    :key="row.sourceLine + '-' + (row.index == null ? 'x' : row.index)"
                    :class="previewRowClass(row)"
                  >
                    <td>{{ row.sourceLine }}行目</td>
                    <td>{{ row.date || '-' }}</td>
                    <td>{{ previewValue(row, 'start') }}</td>
                    <td>{{ previewValue(row, 'end') }}</td>
                    <td>{{ previewValue(row, 'rest') }}</td>
                    <td>{{ attendancePreview(row) }}</td>
                    <td>{{ projectPreview(row) }}</td>
                    <td>
                      <strong>{{ previewStatus(row) }}</strong>
                      <ul v-if="previewMessages(row).length">
                        <li v-for="message in previewMessages(row)" :key="message">{{ message }}</li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="importResult.rows.length > previewRows.length" class="roster-import-preview__more">
              先頭{{ previewRows.length }}行を表示しています。残り{{ importResult.rows.length - previewRows.length }}行も同じ規則で検査済みです。
            </p>
          </section>
        </template>
      </div>

      <template #footer>
        <button type="button" class="btn btn-secondary" @click="modalOpen = false">閉じる</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!canApply"
          @click="applyImport"
        >
          勤務表へ反映
        </button>
      </template>
    </CModal>
  </div>
</template>

<script>
import { japaneseOr } from '@/utils/japaneseText'
import { ROSTER_TEMPLATE_NOTE } from './rosterImportTexts'
import ConstList from '../ConstList'
import {
  ROSTER_IMPORT_FIELDS,
  buildRosterImportResult,
  buildRosterTemplate,
  buildSourceColumns,
  collectUnresolvedValues,
  decodeRosterFile,
  delimiterLabel,
  detectDelimiter,
  detectHeaderRecordIndex,
  isBlankRecord,
  normalizeComparable,
  parseDelimitedText,
  suggestRosterMappings,
  validateColumnMappings
} from '../utils/rosterImport'

export default {
  name: 'RosterFileImport',
  props: {
    year: [String, Number],
    month: [String, Number],
    monthDay: { type: Array, default: () => [] },
    start: { type: Array, default: () => [] },
    end: { type: Array, default: () => [] },
    rest: { type: Array, default: () => [] },
    // 行ごとの「休憩を空欄と同じに扱うか」（勤務表が決める）。出勤を打刻したときに入った休憩は、
    // 利用者が入力した値ではないので、「空欄だけに反映」でも、ファイルの休憩を入れる。
    // 確認の表と、勤務表へ入れる所が、同じ答えを使うために、ここで受け取る。
    restAsBlank: { type: Array, default: () => [] },
    nightRest: { type: Array, default: () => [] },
    // 行ごとの「夜勤休憩を空欄と同じに扱うか」（勤務表が決める）。休憩の打刻が入れた夜勤休憩 00:00
    // （夜の休憩は打刻していない）は、利用者が入力した値ではないので、「空欄だけに反映」でも、
    // ファイルの夜勤休憩を入れる。確認の表と、勤務表へ入れる所が、同じ答えを使う。
    nightRestAsBlank: { type: Array, default: () => [] },
    attendance1: { type: Array, default: () => [] },
    attendance2: { type: Array, default: () => [] },
    remarks: { type: Array, default: () => [] },
    projectId: { type: Array, default: () => [] },
    projectOptions: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false }
  },
  data () {
    const mappings = {}
    ROSTER_IMPORT_FIELDS.forEach(field => { mappings[field.key] = null })
    return {
      // ［標準TSVをダウンロード］のそばの説明。空なら出さない。
      templateNote: ROSTER_TEMPLATE_NOTE,
      modalOpen: false,
      sourceName: '',
      sourceBuffer: null,
      sourceText: '',
      encodingMode: 'auto',
      delimiterMode: 'auto',
      detectedEncoding: '',
      detectedDelimiter: '',
      decodeWarnings: [],
      records: [],
      hasHeader: true,
      headerRecordIndex: 0,
      rowMode: 'date',
      sourceColumns: [],
      mappings,
      attendanceOverrides: {},
      attendance2Overrides: {},
      projectOverrides: {},
      overwriteMode: 'empty',
      fillRestFromSchedule: true,
      fileError: '',
      lastImportMessage: ''
    }
  },
  computed: {
    importFields () {
      return ROSTER_IMPORT_FIELDS
    },
    headerRowOptions () {
      return this.records
        .map((record, index) => ({ record, index }))
        .filter(item => !isBlankRecord(item.record))
        .slice(0, 30)
        .map(item => {
          const preview = item.record.cells
            .slice(0, 5)
            .map(cell => String(cell || '').replace(/\s+/g, ' ').trim())
            .filter(Boolean)
            .join(' / ')
            .slice(0, 80)
          return {
            index: item.index,
            label: `${item.record.line}行目${preview ? `：${preview}` : ''}`
          }
        })
    },
    dataRowCount () {
      const start = this.hasHeader ? Number(this.headerRecordIndex) + 1 : 0
      return this.records.slice(start).filter(record => !isBlankRecord(record)).length
    },
    attendanceChoices () {
      return [
        { key: '__keep__', label: '既存の勤怠を維持', keep: true, aliases: [] },
        {
          key: 'fulltime',
          label: '常勤',
          aliases: ['常勤', '通常', '通常勤務', '出勤', '勤務', '平日', 'work', 'working'],
          attendance1: ConstList.FULLTIME,
          attendance2: null,
          defaultForTime: true
        },
        {
          key: 'holiday',
          label: '休日',
          aliases: ['休日', '公休', '休み', '所定休日', '法定休日', 'holiday', 'off'],
          attendance1: ConstList.HOLIDAY,
          attendance2: null,
          clearActual: true
        },
        {
          key: 'shift',
          label: 'シフト',
          aliases: ['シフト', '時差出勤', '変則勤務', 'shift'],
          attendance1: ConstList.SHIFT,
          attendance2: ConstList.SHIFT,
          allowedAttendance2: [ConstList.SHIFT]
        },
        {
          key: 'morning-work',
          label: '午前出勤＋午後出勤',
          aliases: ['午前出勤'],
          attendance1: ConstList.MORNING_WORK,
          attendance2: ConstList.AFTERNOON_WORK,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_WORK, ConstList.AFTERNOON_PAID, ConstList.AFTERNOON_SUBSTITUTE]
        },
        {
          key: 'afternoon-paid',
          label: '午前出勤＋午後有給',
          aliases: ['午後有給', '午後有休', '午後半休'],
          attendance1: ConstList.MORNING_WORK,
          attendance2: ConstList.AFTERNOON_PAID,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_PAID]
        },
        {
          key: 'afternoon-substitute',
          label: '午前出勤＋午後代休',
          aliases: ['午後代休'],
          attendance1: ConstList.MORNING_WORK,
          attendance2: ConstList.AFTERNOON_SUBSTITUTE,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_SUBSTITUTE]
        },
        {
          key: 'afternoon-special',
          label: '午前出勤＋午後特休',
          aliases: ['午後特休', '午後特別休暇'],
          attendance1: ConstList.MORNING_WORK,
          attendance2: ConstList.AFTERNOON_SPECIAL_HOLIDAY,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_SPECIAL_HOLIDAY]
        },
        {
          key: 'morning-paid',
          label: '午前有給＋午後出勤',
          aliases: ['午前有給', '午前有休', '午前半休'],
          attendance1: ConstList.MORNING_PAID,
          attendance2: ConstList.AFTERNOON_WORK,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_WORK]
        },
        {
          key: 'morning-substitute',
          label: '午前代休＋午後出勤',
          aliases: ['午前代休'],
          attendance1: ConstList.MORNING_SUBSTITUTE,
          attendance2: ConstList.AFTERNOON_WORK,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_WORK]
        },
        {
          key: 'morning-special',
          label: '午前特休＋午後出勤',
          aliases: ['午前特休', '午前特別休暇'],
          attendance1: ConstList.MORNING_SPECIAL_HOLIDAY,
          attendance2: ConstList.AFTERNOON_WORK,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_WORK]
        },
        {
          key: 'early-morning',
          label: '早出出勤＋午後出勤',
          aliases: ['早出', '早出出勤'],
          attendance1: ConstList.EARLY_MORNING,
          attendance2: ConstList.AFTERNOON_WORK,
          requiresAttendance2: true,
          allowedAttendance2: [ConstList.AFTERNOON_WORK]
        },
        {
          key: 'paid',
          label: '有給',
          aliases: ['有給', '有休', '全休', '年休', '有給休暇', '年次有給休暇'],
          attendance1: ConstList.PAID,
          attendance2: null,
          clearActual: true
        },
        {
          key: 'substitute',
          label: '代休',
          aliases: ['代休', '振休'],
          attendance1: ConstList.SUBSTITUTE,
          attendance2: null,
          clearActual: true
        },
        {
          key: 'absence',
          label: '欠勤',
          aliases: ['欠勤', '無給休暇'],
          attendance1: ConstList.DEDUCTION,
          attendance2: null,
          clearActual: true
        },
        {
          key: 'special',
          label: '特休有給',
          aliases: ['特休', '特別休暇', '特休有給', '慶弔休暇'],
          attendance1: ConstList.SPECIAL_HOLIDAY,
          attendance2: null,
          clearActual: true
        }
      ]
    },
    attendance2Choices () {
      return [
        { key: '__keep__', label: '既存の勤怠2を維持', keep: true, aliases: [] },
        { key: 'afternoon-work', label: '午後出勤', aliases: ['午後出勤', '午後勤務'], attendance2: ConstList.AFTERNOON_WORK },
        { key: 'afternoon-paid', label: '午後有給', aliases: ['午後有給', '午後有休', '午後半休'], attendance2: ConstList.AFTERNOON_PAID },
        { key: 'afternoon-substitute', label: '午後代休', aliases: ['午後代休'], attendance2: ConstList.AFTERNOON_SUBSTITUTE },
        { key: 'afternoon-special', label: '午後特休', aliases: ['午後特休', '午後特別休暇'], attendance2: ConstList.AFTERNOON_SPECIAL_HOLIDAY },
        { key: 'shift', label: 'シフト', aliases: ['シフト', 'shift'], attendance2: ConstList.SHIFT }
      ]
    },
    existingRows () {
      return this.monthDay.map((day, index) => ({
        start: this.start[index] || '',
        end: this.end[index] || '',
        rest: this.restAsBlank[index] === true ? '' : (this.rest[index] || ''),
        // 空欄と同じに扱う行でも、画面にいま入っている休憩。「休憩をプロジェクト設定で補完」を外したとき、
        // この休憩がそのまま残るので、休憩が勤務時間を超えるかの検査に使う。
        restOnScreen: this.rest[index] || '',
        nightRest: this.nightRestAsBlank[index] === true ? '' : (this.nightRest[index] || ''),
        attendance1: this.attendance1[index] == null ? '' : this.attendance1[index],
        attendance2: this.attendance2[index] == null ? '' : this.attendance2[index],
        remarks: this.remarks[index] || '',
        projectId: this.projectId[index] == null ? '' : this.projectId[index]
      }))
    },
    mappingErrors () {
      return validateColumnMappings(this.mappings, this.rowMode)
    },
    unresolvedValues () {
      if (!this.records.length) return { attendance1: [], attendance2: [], projects: [] }
      return collectUnresolvedValues(this.records, {
        hasHeader: this.hasHeader,
        headerRecordIndex: this.headerRecordIndex,
        mappings: this.mappings,
        attendanceChoices: this.attendanceChoices,
        attendance2Choices: this.attendance2Choices,
        projectOptions: this.projectOptions
      })
    },
    unresolvedAttendance1 () {
      return this.unresolvedValues.attendance1
    },
    unresolvedAttendance2 () {
      return this.unresolvedValues.attendance2
    },
    unresolvedProjects () {
      return this.unresolvedValues.projects
    },
    importResult () {
      if (!this.records.length) {
        return { rows: [], validRows: [], errors: [], errorCount: 0, warningCount: 0, skippedCount: 0 }
      }
      return buildRosterImportResult({
        records: this.records,
        hasHeader: this.hasHeader,
        headerRecordIndex: this.headerRecordIndex,
        mappings: this.mappings,
        rowMode: this.rowMode,
        selectedYear: Number(this.year),
        selectedMonth: Number(this.month),
        todayKey: this.todayKey(),
        overwriteMode: this.overwriteMode,
        attendanceChoices: this.attendanceChoices,
        attendance2Choices: this.attendance2Choices,
        attendanceOverrides: this.attendanceOverrides,
        attendance2Overrides: this.attendance2Overrides,
        projectOptions: this.projectOptions,
        projectOverrides: this.projectOverrides,
        existingRows: this.existingRows,
        sourceColumns: this.sourceColumns,
        // 補完の選択で、検査に使う休憩が変わる（付け外しのたびに、確認の表を作り直す）。
        fillRestFromSchedule: this.fillRestFromSchedule
      })
    },
    previewRows () {
      return this.importResult.rows.slice(0, 60)
    },
    /** 「ファイル内の名称を変換」の段（3）を出すか。自動で読めない名称があるときだけ。 */
    nameConversionShown () {
      return this.unresolvedAttendance1.length > 0 ||
        this.unresolvedAttendance2.length > 0 ||
        this.unresolvedProjects.length > 0
    },
    /** 「反映方法を選択」の段の番号。名称の変換の段が出ていれば 4、出ていなければ 3。 */
    applyMethodStepNo () {
      return this.nameConversionShown ? 4 : 3
    },
    canApply () {
      return !this.disabled &&
        this.records.length > 0 &&
        this.importResult.errorCount === 0 &&
        this.importResult.validRows.length > 0
    },
    encodingLabel () {
      const labels = {
        'utf-8': 'UTF-8',
        shift_jis: 'Shift_JIS',
        'utf-16le': 'UTF-16 LE',
        'utf-16be': 'UTF-16 BE'
      }
      return labels[this.detectedEncoding] || '-'
    },
    detectedDelimiterLabel () {
      return this.detectedDelimiter ? delimiterLabel(this.detectedDelimiter) : '-'
    }
  },
  methods: {
    todayKey () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    onFileSelected (event) {
      const file = event.target && event.target.files ? event.target.files[0] : null
      event.target.value = ''
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        this.fileError = 'ファイルサイズは5MB以下にしてください。'
        this.records = []
        this.sourceName = file.name
        this.modalOpen = true
        return
      }
      if (/\.(xlsx?|xlsm)$/i.test(file.name)) {
        this.fileError = 'Excel形式（.xlsx）のファイルは直接読み込めません。Excelの「名前を付けて保存」から「CSV UTF-8 (コンマ区切り)」または「テキスト (タブ区切り)」で保存し直してください。'
        this.records = []
        this.sourceName = file.name
        this.modalOpen = true
        return
      }
      this.fileError = ''
      this.sourceName = file.name
      const reader = new FileReader()
      reader.onload = () => {
        this.sourceBuffer = reader.result
        this.parseSource(true)
        this.modalOpen = true
      }
      reader.onerror = () => {
        this.fileError = 'ファイルを読み取れませんでした。'
        this.records = []
        this.modalOpen = true
      }
      reader.readAsArrayBuffer(file)
    },
    reparseSource () {
      this.parseSource(false)
    },
    parseSource (resetHeader) {
      if (!this.sourceBuffer) return
      try {
        const decoded = decodeRosterFile(this.sourceBuffer, this.encodingMode)
        this.sourceText = decoded.text
        this.detectedEncoding = decoded.encoding
        this.decodeWarnings = decoded.warnings || []
        const delimiter = this.delimiterMode === 'auto'
          ? detectDelimiter(decoded.text)
          : this.delimiterMode
        this.detectedDelimiter = delimiter
        this.records = parseDelimitedText(decoded.text, delimiter)
        if (resetHeader) {
          const detectedHeader = detectHeaderRecordIndex(this.records)
          this.hasHeader = detectedHeader >= 0
          this.headerRecordIndex = detectedHeader >= 0 ? detectedHeader : 0
        } else if (this.headerRecordIndex >= this.records.length) {
          this.headerRecordIndex = 0
        }
        this.resetMappings()
        this.fileError = ''
      } catch (error) {
        this.fileError = japaneseOr(error.message, 'ファイルの内容を読み取れませんでした。ファイルの形式を確認してください。')
        this.records = []
        this.sourceColumns = []
      }
    },
    onHeaderModeChanged () {
      if (this.hasHeader) {
        const detectedHeader = detectHeaderRecordIndex(this.records)
        this.headerRecordIndex = detectedHeader >= 0 ? detectedHeader : 0
      } else {
        this.headerRecordIndex = 0
      }
      this.resetMappings()
    },
    resetMappings () {
      this.sourceColumns = buildSourceColumns(this.records, this.hasHeader, this.headerRecordIndex)
      const headers = this.sourceColumns.map(column => column.header || column.label)
      this.mappings = suggestRosterMappings(headers)
      if (!this.hasHeader && this.mappings.date == null) this.rowMode = 'sequence'
      if (this.hasHeader && this.mappings.date != null) this.rowMode = 'date'
      this.attendanceOverrides = {}
      this.attendance2Overrides = {}
      this.projectOverrides = {}
    },
    onMappingChanged () {
      this.attendanceOverrides = {}
      this.attendance2Overrides = {}
      this.projectOverrides = {}
    },
    setOverride (target, key, value) {
      if (!value) {
        this.$delete(target, key)
      } else {
        this.$set(target, key, value)
      }
    },
    applyImport () {
      if (!this.canApply) return
      if (this.importResult.warningCount > 0) {
        const proceed = window.confirm(
          `注意事項が${this.importResult.warningCount}件あります。プレビュー内容で勤務表へ反映しますか？`
        )
        if (!proceed) return
      }
      this.$emit('apply', {
        rows: this.importResult.validRows,
        overwriteMode: this.overwriteMode,
        fillRestFromSchedule: this.fillRestFromSchedule
      })
      this.lastImportMessage = `${this.importResult.validRows.length}日分を勤務表へ反映しました。内容を確認して「保存」を押してください。`
      this.modalOpen = false
    },
    downloadTemplate () {
      const content = buildRosterTemplate(Number(this.year), Number(this.month))
      const blob = new Blob([content], { type: 'text/tab-separated-values;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `PREIKA勤務表_${this.year}${String(this.month).padStart(2, '0')}.tsv`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(url)
    },
    previewValue (row, key) {
      if (!row || !row.values || row.values[key] === undefined) return '-'
      return row.values[key] === '' ? '空欄' : row.values[key]
    },
    attendancePreview (row) {
      if (!row || !row.values) return '-'
      if (row.attendanceLabel || row.attendance2Label) {
        return [row.attendanceLabel, row.attendance2Label].filter(Boolean).join(' / ')
      }
      const first = this.attendanceChoices.find(choice => choice.attendance1 === row.values.attendance1)
      const second = this.attendance2Choices.find(choice => choice.attendance2 === row.values.attendance2)
      if (!first && !second) return '-'
      return [first && first.label, second && second.label].filter(Boolean).join(' / ')
    },
    projectPreview (row) {
      if (!row || !row.values || row.values.projectId === undefined) return '-'
      if (row.projectLabel) return row.projectLabel
      const project = this.projectOptions.find(option => Number(option.id) === Number(row.values.projectId))
      return project ? project.name : String(row.values.projectId)
    },
    previewMessages (row) {
      return [].concat(row.errors || [], row.warnings || [], row.messages || [])
    },
    previewStatus (row) {
      if (row.skipped) return '読飛ばし'
      if (row.errors && row.errors.length) return 'エラー'
      if (row.warnings && row.warnings.length) return '注意'
      return '反映可能'
    },
    previewRowClass (row) {
      if (row.skipped) return 'roster-import-preview__skip'
      if (row.errors && row.errors.length) return 'roster-import-preview__error'
      if (row.warnings && row.warnings.length) return 'roster-import-preview__warning'
      return 'roster-import-preview__success'
    },
    normalizeComparable
  }
}
</script>

<style scoped>
.roster-import-wrapper { min-width: 0; height: 100%; }
.roster-import-card { height: 100%; }
.roster-import-card__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.roster-import-card__body { display: flex; flex-direction: column; }
.roster-import-card__lead { margin: 0 0 14px; font-weight: 700; line-height: 1.7; }
.roster-import-card__actions { display: flex; flex-wrap: wrap; gap: 9px; }
.roster-import-file-button { position: relative; margin: 0; overflow: hidden; }
.roster-import-file-button input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.roster-import-file-button.disabled input { cursor: not-allowed; }
.roster-import-card__support { margin: 14px 0 0; font-size: 12px; line-height: 1.7; }
.roster-import-card__result { margin: auto 0 0; padding-top: 12px; font-size: 12px; font-weight: 700; }
.roster-import-modal__filename { display: block; margin-top: 3px; color: #668096; font-weight: 400; }
.roster-import-section { padding: 20px 0; border-bottom: 1px solid #dbe6ed; }
.roster-import-section:first-child { padding-top: 0; }
.roster-import-section:last-child { border-bottom: 0; }
.roster-import-section__heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
.roster-import-section__heading > div:first-child { display: flex; align-items: center; gap: 9px; }
.roster-import-section__heading h4 { margin: 0; color: #15344d; font-size: 16px; font-weight: 800; }
.roster-import-step { display: inline-flex; width: 28px; height: 28px; align-items: center; justify-content: center; border-radius: 50%; background: #0d67a8; color: #fff; font-size: 12px; font-weight: 800; }
.roster-import-summary { color: #668096; font-size: 12px; }
.roster-import-format-grid, .roster-import-mapping-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 12px; }
.roster-import-format-grid label, .roster-import-mapping-grid label { display: grid; align-content: start; gap: 5px; margin: 0; color: #26455e; font-size: 12px; font-weight: 700; }
.roster-import-format-grid label small { min-height: 17px; color: #73899b; font-size: 12px; font-weight: 400; }
.roster-import-mapping-grid em { margin-left: 5px; color: #c53f4b; font-size: 12px; font-style: normal; }
.roster-import-mapping--disabled { opacity: .55; }
.roster-import-check { display: flex !important; grid-column: auto; align-items: center !important; gap: 8px !important; min-height: 64px; padding: 11px 12px; border: 1px solid #d4e1e9; border-radius: 10px; background: #f8fbfd; }
.roster-import-check input { flex: 0 0 auto; }
.roster-import-notes, .roster-import-alert ul { margin: 10px 0 0; padding-left: 20px; }
.roster-import-alert { margin: 12px 0 0; }
.roster-import-value-groups { display: grid; gap: 14px; }
.roster-import-value-group { padding: 13px; border: 1px solid #d8e5ee; border-radius: 12px; background: #f8fbfd; }
.roster-import-value-group h5 { margin: 0 0 9px; color: #31546e; font-size: 13px; font-weight: 800; }
.roster-import-value-group label { display: grid; grid-template-columns: minmax(160px, .8fr) minmax(220px, 1.2fr); align-items: center; gap: 10px; margin: 7px 0 0; }
.roster-import-value-group label > span { overflow-wrap: anywhere; font-size: 12px; font-weight: 700; }
.roster-import-options { display: grid; gap: 10px; }
.roster-import-options > label { display: flex; align-items: flex-start; gap: 10px; margin: 0; padding: 12px 14px; border: 1px solid #d5e2ea; border-radius: 11px; background: #fff; }
.roster-import-options label > span { display: grid; gap: 3px; }
.roster-import-options strong { color: #24465f; font-size: 12px; }
.roster-import-options small { color: #6d8496; font-size: 12px; line-height: 1.55; }
.roster-import-check--option { min-height: 0; }
.roster-import-counts { display: flex; flex-wrap: wrap; gap: 6px; }
.roster-import-count { padding: 5px 9px; border-radius: 999px; font-size: 12px; font-weight: 800; }
.roster-import-count--success { background: #e6f6ee; color: #197448; }
.roster-import-count--warning { background: #fff4db; color: #8a6515; }
.roster-import-count--error { background: #fde8ea; color: #b63d49; }
.roster-import-preview { max-height: 440px; border: 1px solid #dce6ec; border-radius: 10px; }
.roster-import-preview table { margin: 0; font-size: 12px; }
.roster-import-preview th { position: sticky; top: 0; z-index: 1; background: #edf4f8; color: #24465f; white-space: nowrap; }
.roster-import-preview td { vertical-align: top; white-space: normal; }
.roster-import-preview td:last-child { min-width: 260px; }
.roster-import-preview td ul { margin: 4px 0 0; padding-left: 17px; }
.roster-import-preview__success { background: #fbfffd; }
.roster-import-preview__warning { background: #fffaf0; }
.roster-import-preview__error { background: #fff5f6; }
.roster-import-preview__skip { color: #7b8e9c; background: #f5f7f8; }
.roster-import-preview__more { margin: 8px 0 0; color: #6d8496; font-size: 12px; text-align: right; }
@media (max-width: 767.98px) {
  .roster-import-value-group label { grid-template-columns: minmax(0, 1fr); gap: 5px; }
  .roster-import-section__heading { align-items: flex-start; }
}
</style>
